import { allNonNullCells, shuffle } from './gridHelpers';
import { HeroId } from './heroes';
import { Cell, COLS, Grid, ROWS, TileKind } from './types';

export type PowerResult = {
  clearedCells: Cell[];
  convertedCells: { cell: Cell; newKind: TileKind }[];
  freezeEnemyMs?: number;
  stunAttacks?: number;
  spawnBombs?: Cell[];
};

export type PowerContext = {
  rng: () => number;
};

export type PowerSpec = {
  heroId: HeroId;
  displayName: string;
  bonusDamage: number;
  execute(grid: Grid, ctx: PowerContext): PowerResult;
};

function clearRow(row: number): PowerResult {
  const cleared: Cell[] = [];
  for (let c = 0; c < COLS; c++) cleared.push({ row, col: c });
  return { clearedCells: cleared, convertedCells: [] };
}

function clearDiagonal(): PowerResult {
  const cleared: Cell[] = [];
  const len = Math.min(ROWS, COLS);
  for (let i = 0; i < len; i++) cleared.push({ row: i, col: i });
  return { clearedCells: cleared, convertedCells: [] };
}

function clearColorBeam(grid: Grid): PowerResult {
  const counts = new Map<TileKind, number>();
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      const t = grid[r][c];
      if (!t) continue;
      counts.set(t.kind, (counts.get(t.kind) ?? 0) + 1);
    }
  }
  let targetKind: TileKind | null = null;
  let targetCount = 0;
  for (const [k, n] of counts) {
    if (n > targetCount) {
      targetKind = k;
      targetCount = n;
    }
  }
  const cleared: Cell[] = [];
  if (targetKind) {
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const t = grid[r][c];
        if (t && t.kind === targetKind) cleared.push({ row: r, col: c });
      }
    }
  }
  return { clearedCells: cleared, convertedCells: [] };
}

function webSnare(grid: Grid, rng: () => number): PowerResult {
  const candidates = allNonNullCells(grid).filter((c) => {
    const t = grid[c.row][c.col];
    return t && t.kind !== 'web';
  });
  const picks = shuffle(candidates, rng).slice(0, 5);
  return {
    clearedCells: [],
    convertedCells: picks.map((cell) => ({ cell, newKind: 'web' as TileKind })),
  };
}

function lightningStrike(grid: Grid, rng: () => number): PowerResult {
  const candidates = allNonNullCells(grid);
  const picks = shuffle(candidates, rng).slice(0, 5);
  return { clearedCells: picks, convertedCells: [] };
}

export const POWERS: Record<HeroId, PowerSpec> = {
  'captain-america': {
    heroId: 'captain-america',
    displayName: 'Shield Throw',
    bonusDamage: 2,
    execute: () => clearRow(Math.floor(ROWS / 2)),
  },
  'black-panther': {
    heroId: 'black-panther',
    displayName: 'Claw Slash',
    bonusDamage: 2,
    execute: () => clearDiagonal(),
  },
  'iron-man': {
    heroId: 'iron-man',
    displayName: 'Repulsor Beam',
    bonusDamage: 3,
    execute: (grid) => clearColorBeam(grid),
  },
  'spider-man': {
    heroId: 'spider-man',
    displayName: 'Web Snare',
    bonusDamage: 0,
    execute: (grid, ctx) => webSnare(grid, ctx.rng),
  },
  thor: {
    heroId: 'thor',
    displayName: 'Lightning',
    bonusDamage: 3,
    execute: (grid, ctx) => lightningStrike(grid, ctx.rng),
  },
  hulk: {
    heroId: 'hulk',
    displayName: 'Smash',
    bonusDamage: 12,
    execute: () => ({ clearedCells: [], convertedCells: [] }),
  },
  'doctor-strange': {
    heroId: 'doctor-strange',
    displayName: 'Time Stop',
    bonusDamage: 3,
    execute: () => ({ clearedCells: [], convertedCells: [], freezeEnemyMs: 8000 }),
  },
  'scarlet-witch': {
    heroId: 'scarlet-witch',
    displayName: 'Reality Warp',
    bonusDamage: 2,
    execute: () => {
      const centerRow = Math.floor(ROWS / 2);
      const centerCol = Math.floor(COLS / 2);
      const convertedCells: { cell: Cell; newKind: TileKind }[] = [];
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          convertedCells.push({
            cell: { row: centerRow + dr, col: centerCol + dc },
            newKind: 'red',
          });
        }
      }
      return { clearedCells: [], convertedCells };
    },
  },
  'captain-marvel': {
    heroId: 'captain-marvel',
    displayName: 'Binary Form',
    bonusDamage: 15,
    execute: () => ({ clearedCells: [], convertedCells: [], stunAttacks: 1 }),
  },
  vision: {
    heroId: 'vision',
    displayName: 'Mind Stone',
    bonusDamage: 4,
    execute: (grid, ctx) => {
      const candidates = allNonNullCells(grid);
      const picks = shuffle(candidates, ctx.rng).slice(0, 5);
      return { clearedCells: picks, convertedCells: [], freezeEnemyMs: 5000 };
    },
  },
  'ant-man': {
    heroId: 'ant-man',
    displayName: 'Giant-Man Slam',
    bonusDamage: 10,
    execute: (grid, ctx) => {
      const candidates = allNonNullCells(grid);
      const picks = shuffle(candidates, ctx.rng).slice(0, 2);
      return { clearedCells: [], convertedCells: [], spawnBombs: picks };
    },
  },
  'ghost-spider': {
    heroId: 'ghost-spider',
    displayName: 'Dimension Jump',
    bonusDamage: 5,
    execute: (grid, ctx) => {
      const candidates = allNonNullCells(grid);
      const pick = shuffle(candidates, ctx.rng).slice(0, 1);
      return { clearedCells: [], convertedCells: [], freezeEnemyMs: 4000, spawnBombs: pick };
    },
  },
  spin: {
    heroId: 'spin',
    displayName: 'Venom Strike',
    bonusDamage: 8,
    execute: () => ({ clearedCells: [], convertedCells: [], stunAttacks: 2 }),
  },
  wolverine: {
    heroId: 'wolverine',
    displayName: 'Berserker Rage',
    bonusDamage: 10,
    execute: (_grid, ctx) => {
      const rowIndexes = [0, 1, 2, 3, 4, 5, 6].slice(0, ROWS);
      for (let i = rowIndexes.length - 1; i > 0; i--) {
        const j = Math.floor(ctx.rng() * (i + 1));
        [rowIndexes[i], rowIndexes[j]] = [rowIndexes[j], rowIndexes[i]];
      }
      const picks = rowIndexes.slice(0, 2);
      const cells: Cell[] = [];
      for (const r of picks) {
        for (let c = 0; c < COLS; c++) cells.push({ row: r, col: c });
      }
      return { clearedCells: cells, convertedCells: [], stunAttacks: 1 };
    },
  },
  rocket: {
    heroId: 'rocket',
    displayName: 'Blaster Barrage',
    bonusDamage: 10,
    execute: (grid, ctx) => {
      const candidates = allNonNullCells(grid);
      const picks = shuffle(candidates, ctx.rng).slice(0, 5);
      return { clearedCells: picks, convertedCells: [], stunAttacks: 1 };
    },
  },
  'red-ranger': {
    heroId: 'red-ranger',
    displayName: 'Power Sword Strike',
    bonusDamage: 14,
    execute: () => {
      const r = Math.floor(ROWS / 2);
      const cells: Cell[] = [];
      for (let c = 0; c < COLS; c++) cells.push({ row: r, col: c });
      return { clearedCells: cells, convertedCells: [] };
    },
  },
  'green-ranger': {
    heroId: 'green-ranger',
    displayName: 'Dragon Dagger',
    bonusDamage: 10,
    execute: (grid, ctx) => {
      const cells: Cell[] = [];
      const len = Math.min(ROWS, COLS);
      for (let i = 0; i < len; i++) cells.push({ row: i, col: i });
      const candidates = allNonNullCells(grid);
      const extra = shuffle(candidates, ctx.rng).slice(0, 2);
      return {
        clearedCells: [...cells, ...extra],
        convertedCells: [],
        stunAttacks: 1,
      };
    },
  },
  'pink-ranger': {
    heroId: 'pink-ranger',
    displayName: 'Power Bow Volley',
    bonusDamage: 8,
    execute: (grid, ctx) => {
      const candidates = allNonNullCells(grid);
      const picks = shuffle(candidates, ctx.rng).slice(0, 5);
      return {
        clearedCells: picks,
        convertedCells: [],
        freezeEnemyMs: 2500,
      };
    },
  },
  daredevil: {
    heroId: 'daredevil',
    displayName: 'Billy Club Strike',
    bonusDamage: 8,
    execute: (grid, ctx) => {
      const candidates = allNonNullCells(grid);
      const picks = shuffle(candidates, ctx.rng).slice(0, 6);
      return { clearedCells: picks, convertedCells: [], stunAttacks: 1 };
    },
  },
  'human-torch': {
    heroId: 'human-torch',
    displayName: 'Flame On!',
    bonusDamage: 12,
    execute: (grid) => {
      const cells: Cell[] = [];
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          if (grid[r][c]) cells.push({ row: r, col: c });
        }
      }
      const keep = Math.min(8, cells.length);
      const step = Math.max(1, Math.floor(cells.length / keep));
      const picks: Cell[] = [];
      for (let i = 0; i < cells.length && picks.length < keep; i += step) {
        picks.push(cells[i]);
      }
      return { clearedCells: picks, convertedCells: [] };
    },
  },
};
